'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Check, AlertTriangle, Eye, EyeOff, CheckCircle, Copy, QrCode, X } from 'lucide-react'
import Image from 'next/image'
import QRCode from 'qrcode'

interface ResultCardProps {
  result: {
    url: string
    passphrase?: string
    salt?: string
    iterations?: number
    secretType: 'text' | 'file'
  }
  onReset: () => void
}

export function ResultCard({ result, onReset }: ResultCardProps) {
  const [showResultPassphrase, setShowResultPassphrase] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [showQR, setShowQR] = useState(false)
  const [qrCodeData, setQrCodeData] = useState<string>('')
  const [showPreview, setShowPreview] = useState(false)

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    }
  }

  // Generate QR code when modal opens
  useEffect(() => {
    if (showQR && result.url) {
      QRCode.toDataURL(result.url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#121217',
          light: '#FFFFFF'
        }
      }).then(setQrCodeData).catch(console.error)
    }
  }, [showQR, result.url])

  // Generate preview text
  const getPreviewText = () => {
    if (result.secretType === 'file') {
      return `File: ${result.url.split('/').pop()?.substring(0, 20)}...`
    }
    // For text secrets, we don't have access to the content here
    // But we can show the URL structure
    const urlParts = result.url.split('#')
    if (urlParts.length > 1) {
      return `Secret ID: ${urlParts[0].split('/').pop()?.substring(0, 8)}...`
    }
    return 'Encrypted secret ready to share'
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-card border border-border rounded-card p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-h2 font-mono font-medium text-foreground mb-2">Secret Created!</h2>
          <p className="text-foreground">
            Your {result.secretType} has been encrypted and is ready to share.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-label font-sans text-foreground mb-2">
              Share this link:
            </label>
            <div className="flex gap-2">
              <Input
                value={result.url}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                onClick={() => copyToClipboard(result.url, 'url')}
                variant="secondary"
                className="relative overflow-hidden transition-all duration-200"
              >
                <span className={`flex items-center gap-2 transition-all duration-200 ${copied === 'url' ? 'text-primary' : ''}`}>
                  {copied === 'url' ? (
                    <>
                      <CheckCircle className="w-4 h-4 animate-in fade-in zoom-in duration-200" />
                      <span className="animate-in fade-in slide-in-from-top-1 duration-200">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy</span>
                    </>
                  )}
                </span>
              </Button>
              <Button
                onClick={() => setShowQR(true)}
                variant="secondary"
                className="flex items-center gap-2"
                title="Show QR code"
              >
                <QrCode className="w-4 h-4" />
                <span className="hidden sm:inline">QR</span>
              </Button>
            </div>
          </div>

          {result.passphrase && (
            <div className="bg-warning-yellow/20 border border-warning-yellow/40 rounded-card p-4">
              <h3 className="font-medium text-warning-yellow mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Important: Share passphrase separately
              </h3>
              <p className="text-label text-foreground mb-3">
                The recipient will need this passphrase to decrypt the secret. 
                Share it through a different channel (SMS, phone call, etc.).
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showResultPassphrase ? "text" : "password"}
                    value={result.passphrase}
                    readOnly
                    className="font-mono text-label pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResultPassphrase(!showResultPassphrase)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                  >
                    {showResultPassphrase ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <Button
                  onClick={() => copyToClipboard(result.passphrase!, 'passphrase')}
                  variant="secondary"
                  size="sm"
                  className="relative overflow-hidden transition-all duration-200"
                >
                  <span className={`flex items-center gap-2 transition-all duration-200 ${copied === 'passphrase' ? 'text-primary' : ''}`}>
                    {copied === 'passphrase' ? (
                      <>
                        <CheckCircle className="w-4 h-4 animate-in fade-in zoom-in duration-200" />
                        <span className="animate-in fade-in slide-in-from-top-1 duration-200">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </span>
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <Button onClick={onReset} variant="secondary" className="w-full">
            Create Another Secret
          </Button>
        </div>
      </div>
      
      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-card p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">QR Code for Mobile Sharing</h3>
              <button
                onClick={() => setShowQR(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {qrCodeData ? (
              <div className="flex flex-col items-center space-y-4">
                <div className="bg-white p-4 rounded-sharp">
                  <Image
                    src={qrCodeData}
                    alt="QR Code"
                    width={300}
                    height={300}
                    unoptimized
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Scan this QR code with a mobile device to open the secret link
                </p>
                <Button
                  onClick={() => {
                    const link = document.createElement('a')
                    link.download = 'safemonk-secret-qr.png'
                    link.href = qrCodeData
                    link.click()
                  }}
                  variant="secondary"
                  className="w-full"
                >
                  Download QR Code
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Generating QR code...</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
